import React, { useCallback, useEffect, useState } from 'react';

import sellarModule from '../../../api/modules/seller.module';
import { showToast } from '../../../utils/toast';
import { AdminSellersView } from './AdminSellers.view';

/**
 * Seller applications, the container. Owns fetching, the debounce, pagination
 * and the four write actions. Everything visual lives in
 * `AdminSellers.view.jsx`, which is pure.
 *
 * WHAT THIS FILE USED TO BE
 * -------------------------
 * 660 lines, of which lines 1 to 475 were a commented-out earlier version of
 * the same page. That block is gone. So is the `console.log` of the whole
 * selected seller record on every render, which put a full profile in the
 * browser console on a page an administrator uses.
 *
 * BEHAVIOUR PRESERVED EXACTLY
 * ---------------------------
 * Same endpoint, same `index`/`offset`/`search` params, same page-index maths,
 * same "do not refetch while the detail panel is open" guard, same optimistic
 * local update on reject, same toasts.
 *
 * The old `SellerDetailsCard` needed a pair of ref-based echo guards on its two
 * toggles, because it re-seeded them from a `data` prop the parent refetch kept
 * replacing, so an optimistic set was flipped back for a frame. That mechanism
 * is gone rather than ported: the switches are seeded once when a row is opened
 * and are otherwise owned here, so there is nothing left to fight.
 *
 * ONE ADDITION: search is now debounced here rather than firing a request per
 * keystroke. The old page took the query from the admin header and refetched on
 * every character.
 */
const PAGE_SIZE = 10;

const AdminSellers = () => {
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [selected, setSelected] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [confirmApproveOpen, setConfirmApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);

  const [autoApprove, setAutoApprove] = useState(false);
  const [autoApproveBusy, setAutoApproveBusy] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [availabilityBusy, setAvailabilityBusy] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 500);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPageIndex(0);
  }, [debouncedSearch]);

  const fetchSellers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await sellarModule.getSellarProfiles({
        index: pageIndex,
        offset: PAGE_SIZE,
        search: debouncedSearch,
      });

      if (!res.success) {
        setError(new Error(res.error || 'Failed to load seller applications'));
        return [];
      }

      const details = res.data?.details || res.data;
      const list = Array.isArray(details) ? details : details?.sellers || details?.results || [];
      setSellers(list);
      setTotal(res.data?.total || res.data?.details?.total || list.length);
      return list;
    } catch (caught) {
      setError(caught);
      return [];
    } finally {
      setLoading(false);
    }
  }, [pageIndex, debouncedSearch]);

  // The original guard: a refetch while the detail panel is open would swap the
  // record out from under the reviewer mid-decision.
  useEffect(() => {
    if (selected) return;
    fetchSellers();
  }, [selected, fetchSellers]);

  const openSeller = (seller) => {
    setSelected(seller);
    setAutoApprove(Boolean(seller?.auto_approve));
    setUnavailable(Boolean(seller?.is_store_unavailable));
  };

  const refreshSelected = async () => {
    const list = await fetchSellers();
    if (!selected) return;
    const fresh = list.find((row) => row.id === selected.id);
    if (fresh) setSelected(fresh);
  };

  const handleApprove = async () => {
    if (!selected) return;
    setActionBusy(true);
    try {
      const res = await sellarModule.updateSellerStatus(selected.id, 'approved');
      if (res.success) {
        showToast.success('Seller approved successfully');
        setSellers((prev) =>
          prev.map((row) => (row.id === selected.id ? { ...row, status: 'approved' } : row)),
        );
        setSelected((prev) => (prev ? { ...prev, status: 'approved' } : prev));
        setConfirmApproveOpen(false);
        await refreshSelected();
      } else {
        showToast.error(res.error || 'Failed to approve seller');
      }
    } finally {
      setActionBusy(false);
    }
  };

  const handleReject = async (reason) => {
    if (!selected) return;
    setActionBusy(true);
    try {
      const res = await sellarModule.updateSellerStatus(selected.id, 'rejected', reason);
      if (res.success) {
        showToast.success('Seller rejected successfully');
        setSellers((prev) =>
          prev.map((row) =>
            row.id === selected.id ? { ...row, status: 'rejected', rejection_reason: reason } : row,
          ),
        );
        setSelected((prev) =>
          prev ? { ...prev, status: 'rejected', rejection_reason: reason } : prev,
        );
        setRejectOpen(false);
      } else {
        showToast.error(res.error || 'Failed to reject seller');
      }
    } finally {
      setActionBusy(false);
    }
  };

  const handleAutoApprove = async (enabled) => {
    if (!selected) return;
    const previous = autoApprove;
    setAutoApprove(enabled);
    setAutoApproveBusy(true);
    try {
      const res = await sellarModule.updateSellerAutoApproval(selected.id, enabled);
      if (res.success) {
        // `details`, not the top level. The module hands back `response.data`
        // untouched, and the endpoint answers
        // `{status, message, details:{profile_id, new_status, user_role, auto_approve}}`
        // (apps/api/apps/users/views.py:397-406). Read one level too high and
        // this is always `undefined`, so `?? enabled` always wins and the
        // server can never correct the optimistic value.
        const applied = res.data?.details?.auto_approve ?? enabled;
        setAutoApprove(applied);
        showToast.success(
          applied
            ? 'Auto-approval enabled for this seller'
            : 'Auto-approval disabled for this seller',
        );
      } else {
        setAutoApprove(previous);
        showToast.error(res.error || 'Failed to update auto-approval setting');
      }
    } catch (caught) {
      setAutoApprove(previous);
      showToast.error('Something went wrong');
    } finally {
      setAutoApproveBusy(false);
    }
  };

  const handleAvailability = async (isUnavailable) => {
    if (!selected) return;
    const previous = unavailable;
    setUnavailable(isUnavailable);
    setAvailabilityBusy(true);
    try {
      const res = await sellarModule.updateStoreAvailabilityAdmin(selected.id, isUnavailable);
      if (res.success) {
        // Same shape, a different endpoint — these two are independent views
        // that happen to share the `details` convention, not one contract.
        // `POST /api/users/admin/store/availability/` answers
        // `{success, message, details:{seller_id, is_store_unavailable}}`
        // (apps/api/apps/users/views.py:1195-1202).
        const applied = res.data?.details?.is_store_unavailable ?? isUnavailable;
        setUnavailable(applied);
        showToast.success(
          applied ? 'Store marked as unavailable' : 'Store marked as available',
        );
      } else {
        setUnavailable(previous);
        showToast.error(res.error || 'Failed to update store availability');
      }
    } catch (caught) {
      setUnavailable(previous);
      showToast.error('Something went wrong');
    } finally {
      setAvailabilityBusy(false);
    }
  };

  const page = Math.floor(pageIndex / PAGE_SIZE) + 1;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <AdminSellersView
      sellers={sellers}
      loading={loading}
      error={error}
      onRetry={fetchSellers}
      search={search}
      onSearchChange={setSearch}
      page={page}
      pageCount={pageCount}
      pageSize={PAGE_SIZE}
      total={total}
      onPageChange={(next) => setPageIndex((next - 1) * PAGE_SIZE)}
      selected={selected}
      onOpen={openSeller}
      onClose={() => setSelected(null)}
      onApprove={handleApprove}
      onReject={handleReject}
      onAutoApproveChange={handleAutoApprove}
      onAvailabilityChange={handleAvailability}
      autoApprove={autoApprove}
      autoApproveBusy={autoApproveBusy}
      unavailable={unavailable}
      availabilityBusy={availabilityBusy}
      actionBusy={actionBusy}
      confirmApproveOpen={confirmApproveOpen}
      onConfirmApproveOpen={() => setConfirmApproveOpen(true)}
      onConfirmApproveClose={() => setConfirmApproveOpen(false)}
      rejectOpen={rejectOpen}
      onRejectOpen={() => setRejectOpen(true)}
      onRejectClose={() => setRejectOpen(false)}
    />
  );
};

export default AdminSellers;
